import { Close, Stars } from '@mui/icons-material';
import {
  Alert,
  Grid,
  Skeleton,
  TextField,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from '../../hooks/useForm';
import GenButton from '../GenButton';
import SectionTitle from '../SectionTitle';

const NotesModal = ({
  open,
  handleClose,
  notesHandler,
  noteLoading,
  vendorOrderId,
}) => {
  const { t } = useTranslation('workOrdersTable');

  const [{ note }, handleInputChange, reset] = useForm({
    note: '',
  });

  const disabled = !note.length;

  const handleOnConfirm = () => {
    notesHandler({ note, vendorOrderId });
    reset();
  };
  const handleCloseClick = useCallback(() => {
    handleClose(false);
  }, [handleClose]);

  useEffect(() => {
    if (!noteLoading) {
      handleCloseClick();
    }
  }, [noteLoading, handleCloseClick]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseClick}
      maxWidth="xs"
      fullWidth={true}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme => theme.palette.grey[50],
          marginBottom: 2,
          padding: '8px 16px !important',
        }}
      >
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2">
            {t('detail.notes.popUp.title')}
          </Typography>
          <GenButton
            iconForIconButton={<Close color="action" />}
            onClick={handleCloseClick}
          />
        </Grid>
      </DialogTitle>
      <DialogContent sx={{ padding: [1, 2] }}>
        <DialogContentText sx={{ lineHeight: '10px' }}>
          <Typography variant="overline" sx={{ lineHeight: '0px' }}>
            {t('detail.notes.popUp.label')}
          </Typography>
        </DialogContentText>
        <TextField
          multiline
          minRows={4}
          maxRows={4}
          margin="dense"
          name="note"
          fullWidth
          value={note}
          placeholder={t('detail.notes.popUp.placeholder')}
          onChange={handleInputChange}
          variant="outlined"
          helperText={t('detail.notes.popUp.helperText')}
          FormHelperTextProps={{ style: { margin: '8px 0px' } }}
          inputProps={{
            'data-testid': 'popUpTextArea',
          }}
        />
      </DialogContent>
      <DialogActions sx={{ padding: [1, 2] }}>
        <GenButton
          onClick={handleCloseClick}
          text={t('detail.notes.popUp.buttons.cancel')}
          sx={{
            color: theme => theme.palette.text.primary,
            borderColor: theme => theme.palette.divider,
          }}
        />
        <GenButton
          text={t('detail.notes.popUp.buttons.add')}
          variant="contained"
          onClick={handleOnConfirm}
          loading={noteLoading}
          disabled={disabled}
          data-testid="AddNoteButton"
        />
      </DialogActions>
    </Dialog>
  );
};

const RequestsComponent = ({ data }) => {
  return (
    <Grid container flexDirection="column" spacing={1}>
      {data.map((r, i) => (
        <Grid item key={i}>
          <Alert
            icon={
              <Stars
                sx={{
                  width: '24px',
                  height: '24px',
                  fontSize: '30px',
                }}
              />
            }
            severity="info"
          >
            <Typography variant="body2">{r}</Typography>
          </Alert>
        </Grid>
      ))}
    </Grid>
  );
};

const NoRequestsComponent = () => {
  const { t } = useTranslation('workOrdersTable');
  return (
    <Grid
      container
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{
        backgroundColor: 'rgb(239, 247, 254)',
        width: '100%',
        borderRadius: 2,
        ml: 0,
        mt: 0,
        p: 2,
      }}
    >
      <Grid item>
        <Stars
          sx={{
            width: '60px',
            height: '60px',
            fontSize: '60px',
            color: '#64B6F7',
          }}
        />
      </Grid>
      <Grid item>
        <Typography color="textPrimary" variant="subtitle1">
          {t('detail.noRequests')}
        </Typography>
      </Grid>
    </Grid>
  );
};

const requestsHandler = data =>
  data && data?.length ? (
    <RequestsComponent data={data} />
  ) : (
    <NoRequestsComponent />
  );

const WOrderNotesAndRequests = ({
  data,
  notesHandler = null,
  isLoading,
  noteLoading,
}) => {
  const { t } = useTranslation('workOrdersTable');

  const [openNotesModal, setOpenNotesModal] = useState(false);
  const handleTextAreaClick = () => {
    setOpenNotesModal(true);
  };

  return !isLoading ? (
    <Box
      sx={{
        bgcolor: 'background.paper',
        p: { xs: 2, lg: 4 },
        borderTop: '1px solid',
        borderColor: theme => theme.palette.divider,
      }}
    >
      <Grid container>
        <Grid item xs={12} lg={6}>
          <Grid container flexDirection="column">
            <Box
              pr={{ xs: 0, lg: 2 }}
              pb={{ xs: 2, lg: 0 }}
              sx={{ height: '100%', width: '100%' }}
            >
              <Grid item>
                <SectionTitle
                  title={t('card.hiddenContent.requestsTitle')}
                  subsection={true}
                  marginBottom={1}
                />
              </Grid>
              <Grid item>{requestsHandler(data?.specialRequests)}</Grid>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Grid
            container
            flexDirection="column"
            sx={{
              height: '100%',
              borderLeft: { xs: 0, lg: 1 },
              color: theme => theme.palette.divider,
            }}
          >
            <Box pl={{ xs: 0, lg: 2 }}>
              <Grid item>
                <SectionTitle
                  title={t('detail.notes.notesLabel')}
                  subsection={true}
                  marginBottom={1}
                />
              </Grid>
              <Grid item>
                <TextField
                  name="notesValue"
                  multiline
                  minRows={4}
                  maxRows={4}
                  variant="outlined"
                  margin="dense"
                  value={data?.notes ?? ''}
                  helperText={t('detail.notes.notesHelpText')}
                  sx={{ width: '100%', margin: 0 }}
                  inputProps={{
                    onClick: handleTextAreaClick,
                    sx: {
                      cursor: 'pointer',
                      color: theme => theme.palette.grey[400],
                    },
                    'data-testid': 'readOnlyTextArea',
                    readOnly: true,
                  }}
                />
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <NotesModal
        open={openNotesModal}
        handleClose={setOpenNotesModal}
        notesHandler={notesHandler}
        noteLoading={noteLoading}
        vendorOrderId={data.vendorOrderId}
      />
    </Box>
  ) : (
    <Box
      data-testid="skeleton"
      sx={{
        bgcolor: 'background.paper',
        p: { xs: 2, lg: 4 },
        borderTop: '1px solid',
        borderColor: theme => theme.palette.divider,
      }}
    >
      <Grid container>
        <Grid item xs={12} lg={6}>
          <Grid container flexDirection="column">
            <Box
              pr={{ xs: 0, lg: 2 }}
              pb={{ xs: 2, lg: 0 }}
              sx={{ height: '100%' }}
            >
              <Grid item>
                <Skeleton variant="text" width={120} />
              </Grid>
              <Grid item>
                <Skeleton variant="rounded" width="100%" height={80} />
              </Grid>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Grid
            container
            flexDirection="column"
            sx={{
              height: '100%',
              borderLeft: { xs: 0, lg: 1 },
              color: theme => theme.palette.divider,
            }}
          >
            <Box pl={{ xs: 0, lg: 2 }}>
              <Grid item>
                <Skeleton variant="text" width={120} />
              </Grid>
              <Grid item>
                <Skeleton variant="rounded" width="100%" height={80} />
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WOrderNotesAndRequests;
